import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { TICKER_PLACES } from '@/data/destinations'
import { fetchImages, type Img } from '@/services/images'
import { Attribution } from '@/components/shared/Attribution'
import { EmberField } from '@/components/shared/EmberField'

// Signature destinations used for the floating photo cards. Reverent, still —
// no motion energy, just a slow, barely-perceptible drift.
const SIGNATURE_CARDS = [
  { query: 'Jerusalem Western Wall Old City', label: 'Jerusalem · Israel' },
  { query: 'Camino de Santiago pilgrims path Spain', label: 'Camino · Spain' },
  { query: 'Bhutan Tigers Nest monastery Himalayas', label: 'Bhutan' },
  { query: 'Varanasi Ganges ghats dawn ceremony', label: 'Varanasi · India' },
  { query: 'Mont Saint-Michel abbey tidal island France', label: 'Mont Saint-Michel' },
]

// Cards float in the empty space around the central wordmark — never over
// the headline or the pill, echoing the parent app's landing composition.
const POSITIONS = [
  { top: '8%',  left: '4%',  width: 128 },
  { top: '58%', left: '3%',  width: 112 },
  { top: '6%',  right: '4%', width: 130 },
  { top: '60%', right: '5%', width: 116 },
  { top: '32%', right: '13%', width: 100 },
]

function FloatingCard({ index, img, label, pos }: { index: number; img: Img; label: string; pos: typeof POSITIONS[number] }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, y: [0, -10, 0] }}
      transition={{
        opacity: { duration: 1, delay: 0.3 + index * 0.15 },
        y: { duration: 8 + index, repeat: Infinity, ease: 'easeInOut', delay: index * 0.4 },
      }}
      style={{
        position: 'absolute',
        top: pos.top,
        left: (pos as any).left,
        right: (pos as any).right,
        width: pos.width,
        zIndex: 1,
        display: 'none',
      }}
      className="s-float-card"
    >
      <div style={{ border: '1px solid rgba(201,151,58,0.32)', overflow: 'hidden', background: 'rgba(8,28,16,0.6)', boxShadow: '0 12px 32px rgba(0,0,0,0.5)' }}>
        <img
          src={img.thumb || img.url}
          alt={img.alt || label}
          style={{ width: '100%', height: pos.width * 0.72, objectFit: 'cover', display: 'block', filter: 'saturate(0.7) brightness(0.75)' }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        />
        <div style={{ padding: '6px 9px' }}>
          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.1em', color: 'var(--candle2)', textTransform: 'uppercase' }}>
            {label}
          </div>
          <Attribution image={img} style={{ fontSize: 8, display: 'block', marginTop: 2 }} />
        </div>
      </div>
    </motion.div>
  )
}

export function WelcomeScreen() {
  const setScreen = useAppStore((s) => s.setScreen)
  const ticker = [...TICKER_PLACES, ...TICKER_PLACES]
  const [cardImages, setCardImages] = useState<(Img | null)[]>([null, null, null, null, null])

  useEffect(() => {
    let cancelled = false
    SIGNATURE_CARDS.forEach((card, i) => {
      fetchImages(card.query, 1).then((imgs) => {
        if (cancelled) return
        if (imgs[0]) {
          setCardImages((prev) => {
            const next = [...prev]
            next[i] = imgs[0]
            return next
          })
        }
      })
    })
    return () => { cancelled = true }
  }, [])

  const enter = () => setScreen('mode')

  return (
    <div
      onClick={enter}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') enter() }}
      style={{
        minHeight: '100vh', position: 'relative', overflow: 'hidden', cursor: 'pointer',
        background: 'radial-gradient(ellipse at 50% 38%, rgba(201,151,58,0.07) 0%, transparent 55%), linear-gradient(180deg, #0E2717 0%, #081C10 45%, #050E08 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <EmberField count={26} />

      {cardImages.map((img, i) => img ? (
        <FloatingCard key={i} index={i} img={img} label={SIGNATURE_CARDS[i].label} pos={POSITIONS[i]} />
      ) : null)}

      {/* Central content */}
      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', padding: '0 24px', maxWidth: 720 }}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            border: '1px solid rgba(201,151,58,0.4)', padding: '5px 16px',
            fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.2em',
            color: 'var(--ivory-dim)', marginBottom: 28, textTransform: 'uppercase',
          }}>
          ◈ by Imaginarium · Sacred &amp; Ancestral Journeys
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }}
          style={{
            fontFamily: 'var(--serif-caps)', fontWeight: 600, color: 'var(--ivory)',
            fontSize: 'clamp(40px, 9vw, 88px)', letterSpacing: '0.06em', lineHeight: 1.05, marginBottom: 6,
          }}>
          Sanctum
        </motion.h1>
        <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, fontStyle: 'italic', letterSpacing: '0.14em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 26 }}>
          by Imaginarium
        </div>

        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}
          style={{ fontFamily: 'var(--serif-body)', fontSize: 'clamp(15px, 2vw, 19px)', color: 'var(--ivory-dim)', lineHeight: 1.8, marginBottom: 8, fontStyle: 'italic' }}>
          Where does your soul <span style={{ color: 'var(--candle2)', fontStyle: 'italic' }}>really</span> belong?
        </motion.p>
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }}
          style={{ fontFamily: 'var(--serif-body)', fontSize: 'clamp(14px, 1.6vw, 16px)', color: 'var(--ivory-faint)', lineHeight: 1.8, marginBottom: 40, fontStyle: 'italic' }}>
          Five quiet questions. One sacred path. A journey back to yourself.
        </motion.p>

        <motion.button
          onClick={(e) => { e.stopPropagation(); enter() }}
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.55 }}
          whileTap={{ scale: 0.97 }}
          className="s-glow-pill"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            border: '1px solid var(--candle)', borderRadius: 999, padding: '14px 30px',
            background: 'rgba(201,151,58,0.08)', color: 'var(--candle2)',
            fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.2em',
            textTransform: 'uppercase', cursor: 'pointer',
          }}>
          <span style={{ opacity: 0.7 }}>●</span> Enter in your own time <span style={{ opacity: 0.7 }}>●</span>
        </motion.button>
      </div>

      {/* City ticker */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(5,14,8,0.6)', padding: '10px 0', overflow: 'hidden', borderTop: '1px solid var(--hairline)', zIndex: 2 }}>
        <div className="s-ticker-animate" style={{ display: 'flex', whiteSpace: 'nowrap' }}>
          {ticker.map((place, i) => (
            <span key={i} style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.14em', color: 'var(--candle)', padding: '0 22px', textTransform: 'uppercase', opacity: 0.75 }}>
              {place} <span style={{ opacity: 0.3 }}>◈</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
